import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { AlertTriangle, HelpCircle, Loader2 } from "lucide-react";
import Button from "../components/Button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  /** danger=true → botón rojo variant="danger" + icono rojo */
  danger?: boolean;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ConfirmContext = createContext<ConfirmContextValue>({
  confirm: () => Promise.resolve(false),
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ConfirmState {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmState | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Usamos useRef para que confirm() siempre tenga referencia estable sin
  // depender del estado de `pending`.
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setConfirming(false);
      setPending({ options, resolve });
    });
  }, []);

  const handleConfirm = () => {
    setConfirming(true);
    // Pequeño tick para mostrar el spinner antes de resolver
    setTimeout(() => {
      resolveRef.current?.(true);
      resolveRef.current = null;
      setPending(null);
      setConfirming(false);
    }, 0);
  };

  const handleCancel = () => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setPending(null);
    setConfirming(false);
  };

  const isDanger = pending?.options.danger ?? false;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      {pending && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4"
          onClick={(e) => {
            // Clic en el overlay cierra el modal (= cancelar)
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            {/* Header con icono */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-2 rounded-full flex-shrink-0 ${
                  isDanger ? "bg-red-100" : "bg-primary-100"
                }`}
              >
                {isDanger ? (
                  <AlertTriangle
                    className="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                ) : (
                  <HelpCircle
                    className="h-6 w-6 text-primary-600"
                    aria-hidden="true"
                  />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 leading-snug">
                {pending.options.title}
              </h3>
            </div>

            {/* Mensaje opcional */}
            {pending.options.message && (
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                {pending.options.message}
              </p>
            )}

            {/* Botones */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCancel}
                disabled={confirming}
              >
                {pending.options.cancelText ?? "Cancelar"}
              </Button>
              <Button
                variant={isDanger ? "danger" : "primary"}
                className="flex-1"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    Confirmando...
                  </>
                ) : (
                  (pending.options.confirmText ?? "Confirmar")
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useConfirm() {
  return useContext(ConfirmContext);
}
